import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { get } from 'lodash';
import { LoadingButton } from '@mui/lab';

import { ModalActions } from '../../../../../../../RestoreComponent/ModalActions';
import {
  Purchase,
  RefundReason,
  Refundable,
  LineItem,
  TenderTypes
} from '../Types';
import { longDtFormat } from '@/RestoreComponent/DataGrid/utils';
import { RefundSummary } from './RefundSummary';
import { LineItemSelection } from './LineItemSelection';
import CardLoader from '@/components/CardLoader';
import { Autocomplete } from '@/RestoreComponent/Autocomplete';
import {
  computeRefundables,
  isPackLineItem,
  isRefunded,
  noRefundAvailable,
  refundReasonList
} from './utils';
import { ResponseModal } from './ResponseModal';
import { AccountBalanceTrasferConfirmationModal } from './AccountBalanceTrasferConfirmationModal';
import AuthUser from '@/common/utils/AuthUser';
import {
  ELEVATED_ROLE_ACTIONS,
  useElevatedRoleAction
} from '@/hooks/useElevatedRoleAction';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { callRestGetEndpoint } from '@/common/utils/api';

const useStyles: any = makeStyles(theme => ({
  container: {
    overflowX: 'hidden',
    '& .MuiDialogTitle-root': {
      padding: '4px 24px 16px 24px'
    },
    '& .MuiDialogContent-root': {
      padding: '0 16px 6px 16px'
    },
    '& .MuiPaper-root': {
      minWidth: '900px !important'
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '30px !important'
    },
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none'
    }
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 600,
    height: 40
  },
  loading: {
    width: '100%',
    height: 440,
    backgroundColor: '#fff',
    opacity: 0.6,
    zIndex: 101,
    paddingTop: '20%'
  },
  subTitle: {
    display: 'flex',
    maxWidth: 390,
    lineHeight: '24px',
    justifyContent: 'space-between',
    color: theme.palette.grey[500]
  },
  subTitlePart: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.common.black
  },
  '.MuiInputBase-root': {
    paddingRight: 16
  },
  dd: {
    '& label': {
      top: '-4px'
    },
    '& .MuiInputBase-root': {
      paddingTop: 4,
      paddingBottom: 4
    },
    '& .Mui-disabled': {
      backgroundColor: '#F1F3F9'
    }
  }
}));

type RefundPostParams = {
  purchaseId: number;
  refundPaymentMethods: {
    id: number;
    type: TenderTypes;
    amountToRefund: number;
  }[];
  refundLineItems: {
    id: number;
    amount: number;
    service_administered: boolean;
  }[];
  packItemsToDeduct: {
    id: number;
    count: number;
  }[];
  reason: string | undefined;
  refundingStaffId: string;
  sendReciept: boolean;
};

type RefundModalProps = {
  open: boolean;
  setIsRefundInterfaceOpen: (o: boolean) => void;
  receiptId: number;
  onRefundSuccess: () => void;
};

const RefundModal: React.FC<RefundModalProps> = props => {
  const { open, setIsRefundInterfaceOpen, receiptId, onRefundSuccess } = props;

  const loggedInUser = AuthUser.getAuthUser();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [purchaseLineItems, setPurchaseLineItems] = useState([]);
  const [refundableLineItems, setRefundableLineItems] = useState(null);

  const [refundables, setRefundables] = useState<Refundable[]>([]);
  const [refundAllToAccount, setRefundAllToAccount] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [emailRefundReciept, setEmailRefundReciept] = useState(true);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [refundResponses, setRefundResponses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [accountBalConfirmOpen, setAccountBalConfirmOpen] = useState(false);
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [loading, setLoading] = useState(false);
  const [refundParams, setRefundParams] = useState<
    RefundPostParams | undefined
  >(undefined);

  const { isReady, transport } = useElevatedRoleAction({
    actionType: ELEVATED_ROLE_ACTIONS.REFUND,
    onCancel: () => setIsRefundInterfaceOpen(false),
    promptMessage: "Enter manager's pin to issue refund."
  });

  useEffect(() => {
    if (receiptId) {
      (async () => {
        setLoading(true);
        const res = await callRestGetEndpoint(
          { purchase: receiptId },
          '/pos_refund/get_purchase_for_refund'
        );
        const purchase = res?.data?.purchase;
        setPurchase(purchase);
        const lineItemsForView = purchase?.line_items.map((li: LineItem) =>
          li.is_medical
            ? {
                ...li,
                service_administered: true
              }
            : li
        );
        setPurchaseLineItems(lineItemsForView);
        setRefundableLineItems(
          lineItemsForView.filter(
            (li: LineItem) => !isRefunded(li) && !noRefundAvailable(li)
          )
        );
        setLoading(false);
      })();
      setPurchase(null);
    }
  }, [receiptId]);

  useMemo(() => {
    // refundables are loosely tied to payment purchase methods and represent buckets
    // of return fund types, examples are credit cards, refund to account balance and credit refunds
    if (purchase && purchaseLineItems) {
      setRefundables(
        computeRefundables(
          purchase,
          purchaseLineItems.filter((i: LineItem) => i.selected),
          refundAllToAccount
        )
      );
    }
  }, [purchaseLineItems, refundAllToAccount]);

  // toggles the selection state of the item with id or if id is '' remove all selection or '*' select all
  const updateSelectedLineItems = (
    row: LineItem | undefined,
    checked?: boolean,
    checkAll?: boolean
  ) => {
    let nextLineItems: LineItem[] = [...purchaseLineItems];
    if (checkAll === false) {
      nextLineItems = nextLineItems.map(i => ({
        ...i,
        selected: false
      }));
    } else if (checkAll === true) {
      nextLineItems = nextLineItems.map(i => ({
        ...i,
        selected: !isRefunded(i) && !noRefundAvailable(i)
      }));
    } else if (typeof checked === 'boolean') {
      const targetItemIndex = nextLineItems.findIndex(i => i.id === row.id);
      nextLineItems[targetItemIndex]
        ? (nextLineItems[targetItemIndex].selected = checked)
        : null;
    }
    if (!nextLineItems.some(i => i.selected)) {
      setRefundAllToAccount(false);
    }
    setPurchaseLineItems(nextLineItems);
  };

  const onBundleCountChange = (row: LineItem, count: number) => {
    // update refund amount for any bundle packs according to the refund count selection
    const nextLineItems: LineItem[] = [...purchaseLineItems];
    const targetItemIndex = purchaseLineItems.findIndex(
      (i: LineItem) => i.id === row.id
    );
    const targetItem: LineItem = purchaseLineItems[targetItemIndex];
    if (targetItem?.unused_bundle_count && targetItem.unused_bundle_count > 0) {
      nextLineItems[targetItemIndex] = {
        ...targetItem,
        count_to_refund: count,
        refund_amount:
          targetItem.list_price * (count / targetItem.bundle_count),
        // if the refund amount has been edited then we need to reset it
        edit_amount: undefined
      };
    }
    setPurchaseLineItems(nextLineItems);
  };

  const onLineItemAmountChange = (row: LineItem, amount: number) => {
    const targetLineItemIndex = purchaseLineItems.findIndex(
      rli => rli.id === row.id
    );
    const nextLineItems = [...purchaseLineItems];
    if (nextLineItems[targetLineItemIndex]) {
      nextLineItems[targetLineItemIndex] = {
        ...nextLineItems[targetLineItemIndex],
        edit_amount: amount
      };
      setPurchaseLineItems(nextLineItems);
    }
  };

  const onLineItemServiceGivenChange = (row: LineItem, checked: boolean) => {
    const targetLineItemIndex = purchaseLineItems.findIndex(
      rli => rli.id === row.id
    );
    const nextLineItems = [...purchaseLineItems];
    if (nextLineItems[targetLineItemIndex]) {
      nextLineItems[targetLineItemIndex] = {
        ...nextLineItems[targetLineItemIndex],
        service_administered: checked
      };
      setPurchaseLineItems(nextLineItems);
    }
  };

  const handleRefundSubmit = async () => {
    const params = {
      purchaseId: purchase.id,
      // filter only those with positive amount to refund and remove some props only used in the ui
      refundPaymentMethods: refundables
        .filter(r => r.amountToRefund && r.amountToRefund > 0)
        .map(r => ({
          id: typeof r.id === 'number' ? r.id : Number(r.id),
          type: r.type,
          amountToRefund: parseFloat((r.amountToRefund ?? 0).toFixed(4))
        })),
      refundLineItems: purchaseLineItems
        .filter((li: LineItem) => li.selected)
        .map((li: LineItem) => {
          if (li.is_medical) {
            return {
              id: typeof li.id === 'number' ? li.id : Number(li.id),
              amount: li.edit_amount || li.final_amount,
              service_administered: li.service_administered
            };
          } else {
            return {
              id: typeof li.id === 'number' ? li.id : Number(li.id),
              amount: li.edit_amount || li.final_amount
            };
          }
        }),
      packItemsToDeduct: purchaseLineItems
        .filter(
          (li: LineItem) =>
            li.selected &&
            isPackLineItem(li) &&
            typeof li.count_to_refund === 'number'
        )
        .map((li: LineItem) => ({
          id: typeof li.id === 'number' ? li.id : Number(li.id),
          count: li.count_to_refund as number
        })),
      reason: refundReasonList.find(r => r.id === returnReason)?.value,
      refundingStaffId: loggedInUser?.id ?? '',
      sendReciept: emailRefundReciept
    };

    if (refundAllToAccount) {
      setRefundParams(params);
      setAccountBalConfirmOpen(true);
    } else {
      processRefund(params);
    }
  };

  const processRefund = async (params: RefundPostParams) => {
    setSubmitting(true);
    await transport
      .post('/pos_refund/process_refund/', params, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(res => {
        setRefundResponses(get(res, 'data.refunds', []));
        setResponseModalOpen(true);
        setIsError(false);
      })
      .catch(e => {
        const msg = e?.response?.data?.error;
        console.error(msg);
        enqueueSnackbar(msg, { variant: 'error' });
        setIsError(true);
      })
      .finally(() => {
        setSubmitting(false);
        setRefundParams(undefined);
      });
  };

  const enableIssueRefund =
    purchaseLineItems.filter((li: LineItem) => li.selected).length > 0 &&
    returnReason !== '';
  const showLoading = submitting || loading;

  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason && reason === 'backdropClick') {
            return;
          }
          setIsRefundInterfaceOpen(false);
        }}
        className={classes.container}
      >
        <>
          <DialogTitle variant="h6" component="h2">
            <Grid container className={classes.dialogTitle}>
              Refund Selections
              <Tooltip arrow title="Close">
                <IconButton
                  onClick={() => setIsRefundInterfaceOpen(false)}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid container className={classes.subTitle}>
              {!showLoading && purchase && (
                <>
                  <Grid item>
                    <Typography
                      variant="body4"
                      className={classes.subTitlePart}
                    >
                      {moment(purchase.purchasedate).format(longDtFormat)}
                    </Typography>
                  </Grid>
                  <Grid item>|</Grid>
                  <Grid item className={classes.subTitlePart}>
                    <Typography variant="body4">
                      Invoice {purchase.id}
                    </Typography>
                  </Grid>
                  <Grid item>|</Grid>
                  <Grid item className={classes.subTitlePart}>
                    <Typography variant="body4">
                      Total ${purchase.total.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.subTitlePart}>
                    <Typography variant="body4">
                      {purchase.store.address}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogTitle>
          <DialogContent>
            {showLoading && (
              <Box className={classes.loading}>
                <CardLoader />
              </Box>
            )}
            {!showLoading && purchase && (
              <>
                <Grid item>
                  <LineItemSelection
                    purchaseLineItems={purchaseLineItems}
                    selectableItemCount={refundableLineItems.length}
                    onLineItemSelectionChange={updateSelectedLineItems}
                    onBundleCountChange={onBundleCountChange}
                    onLineItemAmountChange={onLineItemAmountChange}
                    onLineItemServiceGivenChange={onLineItemServiceGivenChange}
                  />
                </Grid>
                <Grid
                  container
                  sx={{ flexDirection: 'row-reverse' }}
                  spacing={2}
                  className={classes.container}
                >
                  <RefundSummary
                    refundables={refundables}
                    purchase={purchase}
                    setRefundAllToAccountBalance={setRefundAllToAccount}
                    refundAllToAccountBalance={refundAllToAccount}
                  />
                </Grid>
                <Grid container spacing={2} className={classes.container}>
                  <Grid item xs={6}>
                    <Autocomplete
                      className={classes.dd}
                      fullWidth
                      label="Reason for Return"
                      options={refundReasonList}
                      getOptionLabel={(o: RefundReason) => o.display}
                      value={returnReason}
                      onChange={(e, o: RefundReason) => setReturnReason(o?.id)}
                      onInputChange={(e, o: RefundReason) =>
                        setReturnReason(o?.id)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      className={classes.dd}
                      fullWidth
                      label="Email Receipt to Customer"
                      options={['yes', 'no']}
                      getOptionLabel={(n: string) => n}
                      value={emailRefundReciept ? 'yes' : 'no'}
                      onChange={(e, n) =>
                        setEmailRefundReciept(n === 'yes' ? true : false)
                      }
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <ModalActions>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                setIsRefundInterfaceOpen(false);
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={!isReady}
              color="primary"
              variant="contained"
              onClick={handleRefundSubmit}
              disabled={!enableIssueRefund || submitting || !isReady}
            >
              Issue Refund
            </LoadingButton>
          </ModalActions>
        </>
      </Dialog>
      {responseModalOpen && (
        <ResponseModal
          refundResponses={refundResponses}
          isOpen={responseModalOpen}
          close={() => {
            setResponseModalOpen(false);
            if (!isError) {
              onRefundSuccess();
              setIsRefundInterfaceOpen(false);
            } else {
              setRefundResponses([]);
            }
          }}
        />
      )}
      {accountBalConfirmOpen && (
        <AccountBalanceTrasferConfirmationModal
          isOpen={accountBalConfirmOpen}
          close={o => {
            setAccountBalConfirmOpen(false);
            if (o && refundParams) {
              processRefund(refundParams);
            }
          }}
        />
      )}
    </>
  );
};

export default RefundModal;
